import { Link } from 'react-router-dom'
import { HomePromoBanners } from '../components/HomePromoBanners'
import { Icon } from '../components/Icon'
import { PartnersMarquee } from '../components/PartnersMarquee'
import { ProductCardGrid } from '../components/ProductCardGrid'
import { TestimonialsCarousel } from '../components/TestimonialsCarousel'
import {
  BlogTeaserSkeleton,
  CategoryTilesSkeleton,
  HeroSkeleton,
  PartnersSkeleton,
  ProductGridSkeleton,
  PromoBannerSkeleton,
  TestimonialsSkeleton,
} from '../components/Skeleton'
import {
  BLOG_TEASER_LIMIT,
  BLOG_TEASER_META,
  HERO_CAROUSEL,
  STOREFRONT_HOME,
  type BannerCtaLinkProps,
} from '../constants/home'
import { cx, formatDate } from '../helpers/format'
import { mapHeroSlides } from '../helpers/mapContent'
import { useBlogPosts } from '../hooks/useBlogPosts'
import { useCatalogProducts } from '../hooks/useCatalogProducts'
import { useHeroCarousel } from '../hooks/useHeroCarousel'
import { useHomeCatalogLimit } from '../hooks/useHomeCatalogLimit'
import { useHomeBanners } from '../hooks/useHomeBanners'
import { useNavCategories } from '../hooks/useNavCategories'
import { usePartnerLogos } from '../hooks/usePartnerLogos'
import { useTestimonials } from '../hooks/useTestimonials'
import '../styles/home-page.scss'

function BannerCtaLink({
  label,
  url,
  external,
  className,
  children,
}: BannerCtaLinkProps) {
  if (external) {
    return (
      <a
        href={url}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
      >
        {children ?? label}
      </a>
    )
  }

  return (
    <Link to={url} className={className} aria-label={label}>
      {children ?? label}
    </Link>
  )
}

export function HomePage() {
  const {
    inRange,
    standard,
    plants,
    loading: productsLoading,
    error: productsError,
  } = useCatalogProducts()
  const homeCatalogLimit = useHomeCatalogLimit()
  const freshProducts = inRange.slice(0, homeCatalogLimit)
  const collectionProducts = standard.slice(0, homeCatalogLimit)
  const plantProducts = plants.slice(0, homeCatalogLimit)
  const { tiles: categoryTiles, loading: categoriesLoading } = useNavCategories()
  const { heroBanners, stripBanners, loading: bannersLoading } = useHomeBanners()
  const {
    recognized: recognizedSection,
    partner: partnerSection,
    loading: partnersLoading,
  } = usePartnerLogos()
  const { testimonials, loading: testimonialsLoading } = useTestimonials()
  const { posts: blogPosts, loading: blogLoading } = useBlogPosts()
  const blogTeaserPosts = blogPosts.slice(0, BLOG_TEASER_LIMIT)

  const slides = mapHeroSlides(heroBanners)
  const { activeIndex, goTo, goNext, goPrev, pause, resume, canNavigate } =
    useHeroCarousel(slides.length)
  const slide = slides[activeIndex] ?? slides[0]

  return (
    <div className="home">
      {bannersLoading ? <HeroSkeleton /> : null}
      {!bannersLoading && slides.length > 0 ? (
        <section
          className="hero"
          onMouseEnter={pause}
          onMouseLeave={resume}
          aria-roledescription="carousel"
          aria-label="Homepage promotions"
        >
          <div className="hero__frame">
            <div className="hero__stage">
              <div
                className={cx('hero__slide', slide.mobileImageUrl && 'has-mobile-art')}
                key={slide.id}
              >
                {slide.ctaUrl ? (
                  <BannerCtaLink
                    label={slide.title}
                    url={slide.ctaUrl}
                    external={slide.ctaIsExternal}
                    className="hero__media-link"
                  >
                    <picture>
                      {slide.mobileImageUrl ? (
                        <source
                          media={`(max-width: ${HERO_CAROUSEL.mobileMaxWidthPx}px)`}
                          srcSet={slide.mobileImageUrl}
                        />
                      ) : null}
                      <img
                        src={slide.imageUrl}
                        alt={slide.title}
                        className="hero__image"
                      />
                    </picture>
                  </BannerCtaLink>
                ) : (
                  <picture>
                    {slide.mobileImageUrl ? (
                      <source
                        media={`(max-width: ${HERO_CAROUSEL.mobileMaxWidthPx}px)`}
                        srcSet={slide.mobileImageUrl}
                      />
                    ) : null}
                    <img
                      src={slide.imageUrl}
                      alt={slide.title}
                      className="hero__image"
                    />
                  </picture>
                )}
              </div>

              {canNavigate ? (
                <>
                  <button
                    type="button"
                    className="hero__nav hero__nav--prev"
                    aria-label="Previous slide"
                    onClick={goPrev}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="hero__nav hero__nav--next"
                    aria-label="Next slide"
                    onClick={goNext}
                  >
                    ›
                  </button>
                </>
              ) : null}
            </div>

            {canNavigate ? (
              <div className="hero__dots" role="tablist" aria-label="Hero slides">
                {slides.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={index === activeIndex}
                    aria-label={`Go to slide ${index + 1}`}
                    className={cx('hero__dot', index === activeIndex && 'is-active')}
                    onClick={() => goTo(index)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {categoriesLoading ? <CategoryTilesSkeleton /> : null}
      {!categoriesLoading && categoryTiles.length > 0 ? (
        <section className="section category-tiles" aria-label="Shop categories">
          <div className="container">
            <div className="category-tiles__grid">
              {categoryTiles.map((item) => (
                <Link
                  key={item.id}
                  to={item.to}
                  className={cx(
                    'category-tile',
                    `tone-${item.tone}`,
                    item.emphasize && 'is-express',
                  )}
                >
                  <span className="category-tile__media" aria-hidden>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" className="category-tile__image" />
                    ) : (
                      <span className="category-tile__icon">
                        <Icon name={item.icon} size={30} strokeWidth={1.5} />
                      </span>
                    )}
                  </span>
                  <span className="category-tile__label">
                    {item.emphasize ? <Icon name="bolt" size={14} filled /> : null}
                    {item.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {!productsLoading && !productsError && freshProducts.length > 0 ? (
        <section className="section catalog-section">
          <div className="container">
            <div className="section__head">
              <div>
                <h2>{STOREFRONT_HOME.inRangeTitle}</h2>
                <p className="section__sub">{STOREFRONT_HOME.inRangeSubtitle}</p>
              </div>
            </div>
            <ProductCardGrid products={freshProducts} />
          </div>
        </section>
      ) : null}

      <section className="section catalog-section">
        <div className="container">
          <div className="section__head">
            <div>
              <h2>{STOREFRONT_HOME.standardTitle}</h2>
              <p className="section__sub">{STOREFRONT_HOME.standardSubtitle}</p>
            </div>
          </div>

          {productsLoading ? <ProductGridSkeleton /> : null}
          {productsError ? (
            <p className="catalog-status catalog-status--error">{productsError}</p>
          ) : null}
          {!productsLoading && !productsError && collectionProducts.length === 0 ? (
            <p className="catalog-status">{STOREFRONT_HOME.standardEmpty}</p>
          ) : null}
          {!productsLoading ? <ProductCardGrid products={collectionProducts} /> : null}
        </div>
      </section>

      {!productsLoading && !productsError && plantProducts.length > 0 ? (
        <section className="section catalog-section">
          <div className="container">
            <div className="section__head">
              <div>
                <h2>{STOREFRONT_HOME.plantsTitle}</h2>
                <p className="section__sub">{STOREFRONT_HOME.plantsSubtitle}</p>
              </div>
            </div>
            <ProductCardGrid products={plantProducts} />
          </div>
        </section>
      ) : null}

      {bannersLoading ? <PromoBannerSkeleton /> : null}
      {!bannersLoading ? <HomePromoBanners banners={stripBanners} /> : null}

      {testimonialsLoading ? <TestimonialsSkeleton /> : null}
      {!testimonialsLoading && testimonials.length > 0 ? (
        <TestimonialsCarousel testimonials={testimonials} />
      ) : null}

      {partnersLoading ? <PartnersSkeleton /> : null}
      {!partnersLoading && recognizedSection ? (
        <PartnersMarquee
          title={recognizedSection.title}
          logos={recognizedSection.logos}
        />
      ) : null}

      {blogLoading ? (
        <section className="section blog-teaser">
          <div className="container">
            <div className="section__head">
              <div>
                <h2>{BLOG_TEASER_META.title}</h2>
                <p className="section__sub">{BLOG_TEASER_META.subtitle}</p>
              </div>
            </div>
            <BlogTeaserSkeleton />
          </div>
        </section>
      ) : null}

      {!blogLoading && blogTeaserPosts.length > 0 ? (
        <section className="section blog-teaser">
          <div className="container">
            <div className="section__head">
              <div>
                <h2>{BLOG_TEASER_META.title}</h2>
                <p className="section__sub">{BLOG_TEASER_META.subtitle}</p>
              </div>
              <Link to={BLOG_TEASER_META.seeAllTo}>
                {BLOG_TEASER_META.seeAllLabel} <span aria-hidden>›</span>
              </Link>
            </div>
            <div className="blog-teaser__grid">
              {blogTeaserPosts.map((post) => (
                <Link key={post.id} to={post.to} className="blog-card">
                  <div className="blog-card__media">
                    {post.coverImageUrl ? (
                      <img src={post.coverImageUrl} alt="" className="blog-card__image" />
                    ) : (
                      <div className="blog-card__placeholder" aria-hidden />
                    )}
                  </div>
                  <div className="blog-card__body">
                    {post.publishedAt ? (
                      <time className="blog-card__date" dateTime={post.publishedAt}>
                        {formatDate(post.publishedAt)}
                      </time>
                    ) : null}
                    <h3>{post.title}</h3>
                    {post.excerpt ? <p>{post.excerpt}</p> : null}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {!partnersLoading && partnerSection ? (
        <PartnersMarquee
          title={partnerSection.title}
          logos={partnerSection.logos}
        />
      ) : null}
    </div>
  )
}
